'use strict';

define('vbsw/private/emulatedServiceWorkerWrapper',['vbsw/private/fetchHandler', 'vbsw/private/offlineManager', 'vbsw/private/utils'],
  (FetchHandler, OfflineManager, Utils) => {
    /**
     * Wrapper class for an emulated service worker instance. This class provides a comprehensive interface for
     * managing service worker-related functionality, including message handling, plugin management, and offline data
     * synchronization.
     */
    class EmulatedServiceWorkerWrapper {
      /**
       * Creates an instance of EmulatedServiceWorkerWrapper.
       *
       * @param {string} scope - The scope of the service worker.
       * @param {Object} config - Configuration object for the service worker.
       */
      constructor(scope, config) {
        this.scope = scope;
        this.config = config;
        this.fetchHandler = new FetchHandler(scope, config);
        this.offlineManager = new OfflineManager(scope, config);
        this.messageHandlers = [];
        this.isEmulated = true;
        // Only wrappers that install global interceptors should restore browser APIs on uninstall.
        this.ownsFetchOverride = false;
        this.ownsXMLHttpRequestOverride = false;
        this.emulatedXMLHttpRequest = null;
      }

      /**
       * Returns the configuration object for the emulated service worker.
       *
       * @returns {Object} The configuration object.
       */
      getConfig() {
        return this.config;
      }

      /**
       * Completely cleans up the emulated service worker instance. It uninstalls plugins, offline handlers,
       * and restores the original browser fetch functionality.
       *
       * @returns {Promise<void>} A promise that resolves when the cleanup is complete.
       */
      uninstall() {
        return this.uninstallPlugins()
          .then(() => this.offlineManager.uninstallOfflineHandlers())
          .then(() => {
            if (this.ownsFetchOverride) {
              // restore browser fetch only from the wrapper that installed the global interceptor
              globalThis.fetch = this.config.browserFetch;
            }

            if (this.ownsXMLHttpRequestOverride && globalThis.XMLHttpRequest === this.emulatedXMLHttpRequest) {
              // Restore XHR only if it still points at our adapter; OPT or another library may have replaced it later.
              globalThis.XMLHttpRequest = this.config.browserXMLHttpRequest;
            }

            // clean up the message handler
            if (this.messageEventListener) {
              this.removeMessageEventListener(this.messageEventListener);
              this.messageEventListener = null;
              this.messageHandlers = [];
            }
          });
      }

      /**
       * Installs a message handler with the emulated service worker.
       *
       * @param {Object} messageHandler - The message handler object to install. This object should contain methods
       *                                  that can be invoked by the service worker.
       */
      installMessageHandler(messageHandler) {
        if (!this.messageEventListener) {
          this.messageEventListener = (event) => {
            const { data } = event;
            if (typeof data === 'object' && data.method) {
              const { method } = data;
              const args = data.args || [];
              const client = event.ports[0];

              // invoke the method on the last handler that can handle the message to simulate overriding behavior
              const invoked = this.messageHandlers.reverse().some((handler) => {
                if (typeof handler[method] === 'function') {
                  Utils.postResponse(client, handler[method](...args), `${method}`);
                  return true;
                }
                return false;
              });

              // reject the message if no message handler is found
              if (!invoked) {
                // eslint-disable-next-line prefer-promise-reject-errors
                Utils.postResponse(client, Promise.reject('No message handler found.'), `${method}`);
              }
            }
          };
          this.addMessageEventListener(this.messageEventListener);
        }

        this.messageHandlers.push(messageHandler);
      }

      /**
       * Uninstalls a previously installed message handler.
       *
       * @param {Object} messageHandler - The message handler object to uninstall.
       */
      uninstallMessageHandler(messageHandler) {
        const index = this.messageHandlers.indexOf(messageHandler);
        if (index > -1) {
          this.messageHandlers.splice(index, 1);
        }

        // remove the message event listener if there's no more message handlers left
        if (this.messageHandlers.length === 0 && this.messageEventListener) {
          this.removeMessageEventListener(this.messageEventListener);
          this.messageEventListener = null;
        }
      }

      /**
       * Registers a message event listener on the window object.
       *
       * @param {(event: MessageEvent) => void} listener - The listener function to register.
       */
      // eslint-disable-next-line class-methods-use-this
      addMessageEventListener(listener) {
        // install the message listener on window
        window.addEventListener('message', listener, false);
      }

      /**
       * Unregisters a previously registered message event listener.
       *
       * @param {(event: MessageEvent) => void} listener - The listener function to unregister.
       */
      // eslint-disable-next-line class-methods-use-this
      removeMessageEventListener(listener) {
        window.removeEventListener('message', listener);
      }

      /**
       * Installs plugins for the service worker.
       *
       * @param {string[]} plugins - An array of URLs for the plugins to install.
       * @param {boolean} [reload=true] - If true, forces reloading of all plugins. Otherwise, only reloads plugins
       *                                  whose metadata has changed.
       * @returns {Promise<void>} A promise that resolves when the plugin installation is complete.
       */
      installPlugins(plugins, reload = true) {
        return this.fetchHandler.installPlugins(plugins, reload);
      }

      /**
       * Uninstalls currently installed plugins. This is primarily used for testing purposes.
       *
       * @returns {Promise<void>} A promise that resolves when the plugins are uninstalled.
       */
      uninstallPlugins() {
        return this.fetchHandler.uninstallPlugins();
      }

      /**
       * Activates URL mapping for the service worker.
       *
       * @returns {Promise<void>} A promise that resolves when URL mapping is activated.
       */
      activateUrlMapping() {
        return Promise.resolve(this.fetchHandler.activateUrlMapping());
      }

      /**
       * Handles an intercepted request using the fetch handler. This method is typically called by the
       * PersistenceManager.
       *
       * @param {Request} request - The intercepted request object.
       * @returns {Promise<Response>} A promise that resolves with the response to the request.
       */
      handleRequest(request) {
        return this.fetchHandler.handleRequest(request);
      }

      /**
       * Load and install the offline handler. If the application wants to use the offline toolkit to handle requests,
       * it needs to implement a createOfflineHandler method on app-flow.js that returns an object with a handleRequest
       * method. For example:
       *
       * {
       *   handleRequest: function (request) {
       *     var responseProxy = DefaultResponseProxy.getResponseProxy(options);
       *     return responseProxy.processRequest(request);
       *   }
       * }
       *
       * @param {string} handlerUrl - The URL from which to load the offline handler.
       * @returns {Promise<void>} A promise that resolves when the offline handler is installed.
       */
      installOfflineHandler(handlerUrl) {
        return this.offlineManager.installOfflineHandler(handlerUrl);
      }

      /**
       * Uninstall offline handler.
       *
       * @returns {Promise<void>} A promise that resolves when the offline handler is uninstalled.
       */
      uninstallOfflineHandler() {
        return this.offlineManager.uninstallOfflineHandler();
      }

      /**
       * Install a secondary offline handler defined in an app UI.
       *
       * @param {string} handlerUrl - The URL from which to load the secondary offline handler.
       * @returns {Promise<void>} A promise that resolves when the secondary offline handler is installed.
       */
      installSecondaryOfflineHandler(handlerUrl) {
        return this.offlineManager.installSecondaryOfflineHandler(handlerUrl);
      }

      /**
       * Uninstall the secondary offline handler.
       *
       * @returns {Promise<void>} A promise that resolves when the secondary offline handler is uninstalled.
       */
      uninstallSecondaryOfflineHandler() {
        return this.offlineManager.uninstallSecondaryOfflineHandler();
      }

      /**
       * Perform a sync of cached offline data.
       *
       * @param {Object} options - Options to be passed to the persistence sync manager.
       * @returns {Promise<void>} A promise that resolves when the synchronization is complete.
       */
      syncOfflineData(options) {
        return this.offlineManager.syncOfflineData(options);
      }

      /**
       * Return the offline sync log.
       *
       * @returns {Promise<Object>}} A promise that resolves with the offline sync log.
       */
      getOfflineSyncLog() {
        return this.offlineManager.getOfflineSyncLog();
      }

      /**
       * Insert a new request into the offline sync log.
       *
       * @param {Request} request - The request to insert into the offline sync log.
       * @param {Object} options - Additional options for the insertion.
       * @returns {Promise<void>} A promise that resolves when the request is inserted.
       */
      insertOfflineSyncRequest(request, options) {
        return this.offlineManager.insertOfflineSyncRequest(request, options);
      }

      /**
       * Delete a request from the offline sync log.
       *
       * @param {string|number} requestId - The ID of the request to delete from the offline sync log.
       * @returns {Promise<void>} A promise that resolves when the request is removed.
       */
      removeOfflineSyncRequest(requestId) {
        return this.offlineManager.removeOfflineSyncRequest(requestId);
      }

      /**
       * Update a request in the offline sync log.
       *
       * @param {string|number} requestId - The ID of the request to update in the offline sync log.
       * @param {Request} request - The updated request object.
       * @returns {Promise<void>} A promise that resolves when the request is updated.
       */
      updateOfflineSyncRequest(requestId, request) {
        return this.offlineManager.updateOfflineSyncRequest(requestId, request);
      }

      /**
       * If the flag is true, force offline and vice versa.
       *
       * @param {boolean} flag - If true, forces offline mode. If false, allows online operation.
       * @returns {Promise<void>} A promise that resolves when the offline mode is set.
       */
      forceOffline(flag) {
        return this.offlineManager.forceOffline(flag);
      }

      /**
       * Set options on the offline handler.
       *
       * @param {Object} options - The options to be set on the offline handler.
       * @returns {Promise<void>} A promise that resolves when the options are set.
       */
      setOfflineHandlerOptions(options) {
        return this.offlineManager.setOfflineHandlerOptions(options);
      }

      /**
       * Set options on the secondary offline handler.
       *
       * @param {Object} options - The options to be set on the secondary offline handler.
       * @returns {Promise<void>} A promise that resolves when the options are set.
       */
      setSecondaryOfflineHandlerOptions(options) {
        return this.offlineManager.setSecondaryOfflineHandlerOptions(options);
      }

      /**
       * Ask the FetchHandler for its online status.
       *
       * @returns {Promise<boolean>} A promise that resolves with a boolean indicating whether the offline manager
       *                              is online.
       */
      isOnline() {
        return this.offlineManager.isOnline();
      }

      /**
       * This method is kept to maintain backwards compatibility with test-release-pwa.
       * It does not perform any actual unregister operation.
       *
       * @returns {Promise<void>} A resolved promise.
       */
      // eslint-disable-next-line class-methods-use-this
      unregister() {
        return Promise.resolve();
      }
    }

    return EmulatedServiceWorkerWrapper;
  });

